import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PremiumCTA } from "@/components/PremiumCTA";
import { PublicStats } from "@/components/PublicStats";
import { EstudarPara } from "@/components/EstudarPara";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getEstudo } from "@/lib/estudo";
import { ArrowRight, Target, ListChecks, FileText, BarChart3, Crown, Infinity as InfinityIcon, Brain, BookOpen, CalendarDays, Sparkles, Loader2, Bot } from "lucide-react";

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [perf, setPerf] = useState(null);
  const [disciplinas, setDisciplinas] = useState([]);
  const [adaptive, setAdaptive] = useState(null);
  const [incidence, setIncidence] = useState(null);
  const [errors, setErrors] = useState(null);
  const [materials, setMaterials] = useState(null);
  const [estudo, setEstudoState] = useState(getEstudo());
  const [prof, setProf] = useState({ open: false, text: "", loading: false });

  const abrirProfessor = async (mode) => {
    if (!user?.is_premium) {
      navigate("/premium");
      return;
    }
    setProf({ open: true, text: "", loading: true });

    try {
      const r = await api.post("/ia/professor", { mode });
      setProf({ open: true, text: r.data.resposta || "", loading: false });
    } catch (e) {
      setProf({
        open: true,
        text: e.response?.data?.detail || "Não foi possível consultar o Professor ApoiaConcursos IA.",
        loading: false,
      });
    }
  };

  useEffect(() => {
    const selected = getEstudo();

    api.get("/questions/daily-usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/performance").then((r) => setPerf(r.data)).catch(() => {});
    api.get("/questions/filters").then((r) => setDisciplinas(r.data.disciplinas || [])).catch(() => {});

    api.get("/study/adaptive", {
      params: {
        banca: selected.banca,
        concurso: selected.concurso,
      },
    }).then((r) => setAdaptive(r.data)).catch(() => {});

    api.get("/analytics/incidence", {
      params: {
        banca: selected.banca,
        concurso: selected.concurso,
      },
    }).then((r) => setIncidence(r.data)).catch(() => {});

    api.get("/errors").then((r) => setErrors(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});

    setEstudoState(selected);
  }, []);

  const premium = user?.is_premium;

  const nivel = useMemo(() => {
    const total = perf?.total || 0;
    const disciplinasCount = perf?.disciplinas?.length || 0;
    const streak = perf?.streak || 0;

    if (!total) {
      return {
        label: "Começando agora",
        description: "Resolva questões para o ApoiaConcursos conhecer seu perfil.",
      };
    }

    if (total <= 100) {
      return {
        label: "Iniciante",
        description: "Você está construindo sua base de preparação.",
      };
    }

    if (total <= 500) {
      return {
        label: "Intermediário",
        description: "Você já tem uma base. Agora é hora de ganhar consistência.",
      };
    }

    return {
      label: "Avançado",
      description: "Você já acumulou um histórico amplo de preparação.",
    };
  }, [perf]);

  const prioridade = adaptive?.recomendacoes
    ?.filter((r) => r.total >= 15)
    ?.sort((a, b) => a.aproveitamento - b.aproveitamento)[0] || null;
  const errorTotal = errors?.total || 0;

  const mission = useMemo(() => {
    if (prioridade) {
      return {
        title: `Reforçar ${prioridade.disciplina}`,
        description:
          prioridade.confianca === "baixa"
            ? "Resolva mais questões para medir melhor essa disciplina."
            : `${prioridade.aproveitamento}% de aproveitamento em ${prioridade.total} questões. A prioridade considera seu desempenho e a incidência no banco.`,
        button: "Começar missão",
        to: `/questoes?disciplina=${encodeURIComponent(prioridade.disciplina)}`,
      };
    }

    if (errorTotal > 0) {
      return {
        title: "Revisar seu caderno de erros",
        description: `Você tem ${errorTotal} questão(ões) para revisar.`,
        button: "Revisar erros",
        to: "/erros",
      };
    }

    return {
      title: "Começar sua preparação",
      description:
        "Resolva suas primeiras questões para o ApoiaConcursos identificar seu desempenho.",
      button: "Resolver questões",
      to: "/questoes",
    };
  }, [prioridade, errorTotal]);

  const foco =
    estudo.banca !== "Todas" || estudo.concurso !== "Todas"
      ? estudo.label ||
        `${estudo.banca}${estudo.concurso !== "Todas" ? ` · ${estudo.concurso}` : ""}`
      : null;

  const goDisciplina = (d) => {
    const est = getEstudo();

    navigate(
      `/questoes?disciplina=${encodeURIComponent(d)}&banca=${encodeURIComponent(
        est.banca || "Todas"
      )}&concurso=${encodeURIComponent(est.concurso || "Todas")}`
    );
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
      <section className="vc-fade-up overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-xl">
        <img
          src="/images/banner-apoiaconcursos.jpeg"
          alt="ApoiaConcursos - preparação para os principais concursos"
          className="block h-auto w-full object-contain"
        />
      </section>

      <div className="vc-fade-up">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-1 text-sm font-semibold text-emerald-400">
              Seu treinador para aprovação
            </p>
            <h1 className="text-2xl font-extrabold text-white sm:text-3xl">
              Olá, {user?.name?.split(" ")[0] || "concurseiro"} 👋
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-400">
              {nivel.description}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              Nível do concurseiro
            </p>
            <p className="mt-1 text-lg font-extrabold text-white">
              {nivel.label}
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
          <p className="text-sm">
            <span className="text-slate-400">Seu foco: </span>
            <strong className="text-emerald-300">
              {foco || "Defina o concurso que você quer alcançar em “Estudar para”."}
            </strong>
          </p>
        </div>
      </div>

      {!premium && <PremiumCTA />}

      {premium && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-5">
          <Crown className="h-6 w-6 text-amber-300" />
          <div>
            <p className="font-bold text-amber-300">Premium ativo</p>
            <p className="text-xs text-slate-400">Você tem acesso ilimitado a questões e simulados. Bons estudos!</p>
          </div>
        </div>
      )}

      <PublicStats />

      <section>
        <div className="mb-3 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500/15">
            <Target className="h-4 w-4 text-emerald-400" />
          </span>
          <h2 className="text-lg font-bold text-white">Missão de hoje</h2>
        </div>

        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-slate-900 to-slate-900 p-5 sm:p-6">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                Próximo passo
              </p>
              <h2 className="mt-1 text-xl font-extrabold text-white">
                {mission.title}
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-400">
                {mission.description}
              </p>
            </div>

            <button
              onClick={() => navigate(mission.to)}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-extrabold text-slate-950 transition-colors hover:bg-emerald-400"
            >
              {mission.button}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <div className="mb-3 flex items-center gap-2">
            <Target className="h-5 w-5 text-emerald-400" />
            <h2 className="text-lg font-bold text-white">Continuar estudando</h2>
          </div>

          {foco ? (
            <>
              <p className="text-sm font-bold text-white">{foco}</p>
              <p className="mt-1 text-xs text-slate-500">
                Continue pelas questões alinhadas ao seu objetivo.
              </p>
              <button
                onClick={() => navigate("/questoes")}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-slate-700"
              >
                Continuar
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-400">
                Você ainda não definiu um objetivo específico.
              </p>
              <button
                onClick={() => {
                  const el = document.querySelector('[data-testid="estudar-para"]');
                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-700"
              >
                Escolher objetivo
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          )}
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-white">Sua evolução</h2>
            <button
              onClick={() => navigate("/desempenho")}
              className="flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-emerald-400"
            >
              Ver desempenho
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-800/60 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Questões
              </p>
              <p className="mt-1 text-2xl font-extrabold text-white">
                {perf?.total ?? 0}
              </p>
            </div>

            <div className="rounded-xl bg-slate-800/60 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Acerto
              </p>
              <p className="mt-1 text-2xl font-extrabold text-white">
                {perf?.accuracy ?? 0}%
              </p>
            </div>

            <div className="rounded-xl bg-slate-800/60 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Corretas
              </p>
              <p className="mt-1 text-2xl font-extrabold text-white">
                {perf?.correct ?? 0}
              </p>
            </div>

            <div className="rounded-xl bg-slate-800/60 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Sequência
              </p>
              <p className="mt-1 text-2xl font-extrabold text-white">
                {perf?.streak ?? 0}
              </p>
              <p className="mt-1 text-[10px] text-slate-500">dias estudando</p>
            </div>

            <div className="col-span-2 rounded-xl bg-slate-800/60 p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Uso diário
              </p>

              {premium ? (
                <div className="mt-1 flex items-center gap-2 text-amber-300">
                  <InfinityIcon className="h-5 w-5" />
                  <span className="text-lg font-extrabold">Ilimitado</span>
                </div>
              ) : (
                <>
                  <div className="mt-1 flex items-end justify-between gap-3">
                    <p className="text-2xl font-extrabold text-white">
                      {usage ? usage.count : 0}
                      <span className="text-sm text-slate-500">
                        /{usage?.limit ?? 10}
                      </span>
                    </p>
                    <span className="text-[10px] text-slate-500">
                      questões grátis hoje
                    </span>
                  </div>

                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-700">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all"
                      style={{
                        width: `${Math.min(
                          100,
                          ((usage?.count ?? 0) / (usage?.limit ?? 10)) * 100
                        )}%`,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-white">O que mais cai</h2>
            <button
              onClick={() => navigate("/incidencia")}
              className="flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-emerald-400"
            >
              Ver incidência
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {incidence?.mais_caem?.length ? (
            <div className="space-y-2">
              {incidence.mais_caem.slice(0, 3).map((item, index) => (
                <button
                  key={`${item.assunto}-${index}`}
                  onClick={() => navigate("/incidencia")}
                  className="flex w-full items-center justify-between rounded-xl bg-slate-800/60 px-4 py-3 text-left hover:bg-slate-800"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">
                      {item.assunto}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {item.disciplina} · {item.questoes} questões
                    </p>
                  </div>

                  <span className="ml-3 shrink-0 text-xs font-bold text-emerald-300">
                    {item.percentual}%
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              Ainda não há dados suficientes de incidência para esta seleção.
            </p>
          )}
        </section>
      </div>

      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-white">Precisa reforçar</h2>
          <button
            onClick={() => navigate("/plano")}
            className="flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-emerald-400"
          >
            Ver plano completo
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {adaptive?.recomendacoes?.length ? (
          <div className="grid gap-3 md:grid-cols-3">
            {adaptive.recomendacoes.slice(0, 3).map((r) => (
              <button
                key={r.disciplina}
                onClick={() => goDisciplina(r.disciplina)}
                className="rounded-2xl border border-slate-800 bg-slate-900 p-5 text-left transition-all hover:border-amber-400/40 hover:bg-slate-800/70"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-white">{r.disciplina}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {r.total} questões respondidas
                    </p>
                  </div>

                  <span className="rounded-full bg-amber-400/10 px-2 py-1 text-[10px] font-bold uppercase text-amber-300">
                    {r.confianca}
                  </span>
                </div>

                <div className="mt-4 flex items-end justify-between">
                  <span className="text-2xl font-extrabold text-white">
                    {r.aproveitamento}%
                  </span>
                  <span className="text-xs text-slate-500">
                    prioridade {r.prioridade}
                  </span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <p className="text-sm text-slate-400">
              Ainda não há dados suficientes para recomendar pontos de reforço.
              Resolva questões para o plano adaptativo começar a trabalhar.
            </p>
          </div>
        )}
      </section>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { label: "Resolver Questões", desc: "Pratique por disciplina", icon: ListChecks, to: "/questoes", tid: "home-go-questoes" },
          { label: "Simulados", desc: "Provas cronometradas", icon: FileText, to: "/simulados", tid: "home-go-simulados" },
          { label: "Desempenho", desc: "Acompanhe sua evolução", icon: BarChart3, to: "/desempenho", tid: "home-go-desempenho" },
        ].map((c) => (
          <button key={c.to} data-testid={c.tid} onClick={() => navigate(c.to)}
            className="group flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-5 text-left transition-all hover:border-emerald-500/40 hover:bg-slate-800/60">
            <span className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-500/15 text-emerald-400 transition-transform group-hover:scale-110">
              <c.icon className="h-6 w-6" />
            </span>
            <div>
              <p className="font-bold text-white">{c.label}</p>
              <p className="text-xs text-slate-500">{c.desc}</p>
            </div>
          </button>
        ))}
      </div>

      <section className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-white">Caderno de erros</h2>
            <button
              onClick={() => navigate("/erros")}
              className="flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-emerald-400"
            >
              Revisar erros
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="rounded-xl bg-slate-800/60 p-5">
            <p className="text-3xl font-extrabold text-white">
              {errors?.total ?? 0}
            </p>
            <p className="mt-1 text-sm text-slate-400">
              {errors?.total === 1
                ? "questão para revisar"
                : "questões para revisar"}
            </p>
          </div>

          <button
            onClick={() => navigate("/erros")}
            className="mt-3 w-full rounded-xl border border-slate-700 px-4 py-2.5 text-sm font-bold text-white hover:border-emerald-500/40 hover:bg-slate-800"
          >
            Abrir caderno de erros
          </button>
      </section>

      <EstudarPara />

      <section>
        <div className="mb-3">
          <h2 className="text-lg font-bold text-white">Recursos de estudo</h2>
          <p className="text-sm text-slate-500">
            Ferramentas para reforçar sua preparação.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <button
            onClick={() => navigate("/mapas-mentais")}
            className="group flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-5 text-left transition-all hover:border-emerald-500/40 hover:bg-slate-800/60"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-500/15 text-emerald-400">
              <Brain className="h-6 w-6" />
            </span>
            <div>
              <p className="font-bold text-white">Mapas Mentais</p>
              <p className="text-xs text-slate-500">Revise conteúdos de forma visual</p>
            </div>
          </button>

          <button
            onClick={() => navigate("/materiais")}
            className="group flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-5 text-left transition-all hover:border-emerald-500/40 hover:bg-slate-800/60"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-500/15 text-emerald-400">
              <BookOpen className="h-6 w-6" />
            </span>
            <div>
              <p className="font-bold text-white">Materiais PDFs</p>
              <p className="text-xs text-slate-500">
                {materials?.materials?.length ?? 0} materiais disponíveis
              </p>
            </div>
          </button>

          <button
            onClick={() => navigate("/plano")}
            className="group flex items-center gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-5 text-left transition-all hover:border-emerald-500/40 hover:bg-slate-800/60"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-500/15 text-emerald-400">
              <CalendarDays className="h-6 w-6" />
            </span>
            <div>
              <p className="font-bold text-white">Plano de Estudos</p>
              <p className="text-xs text-slate-500">Acompanhe seu plano adaptativo</p>
            </div>
          </button>
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">Disciplinas</h2>
            <p className="text-sm text-slate-500">
              Escolha uma disciplina para começar a praticar.
            </p>
          </div>

          <button
            onClick={() => navigate("/questoes")}
            className="flex shrink-0 items-center gap-1 text-xs font-semibold text-slate-400 hover:text-emerald-400"
          >
            Ver todas
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        {disciplinas.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {disciplinas.slice(0, 6).map((disciplina) => (
              <button
                key={disciplina}
                onClick={() =>
                  navigate(`/questoes?disciplina=${encodeURIComponent(disciplina)}`)
                }
                className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 p-4 text-left transition-all hover:border-emerald-500/40 hover:bg-slate-800/60"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-emerald-500/15 text-emerald-400">
                  <BookOpen className="h-5 w-5" />
                </span>
                <span className="min-w-0 text-sm font-semibold text-white">
                  {disciplina}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-5 text-sm text-slate-500">
            Nenhuma disciplina disponível.
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-indigo-500/20 bg-slate-900 p-5">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-indigo-500/15 text-indigo-300">
              <Sparkles className="h-6 w-6" />
            </span>

            <div>
              <h2 className="text-lg font-bold text-white">
                Professor ApoiaConcursos IA
              </h2>
              <p className="mt-1 max-w-xl text-sm text-slate-400">
                Use seu desempenho real para identificar pontos fortes, dificuldades e prioridades de estudo.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              onClick={() => abrirProfessor("analisar_desempenho")}
              className="rounded-xl border border-indigo-500/30 px-4 py-2.5 text-sm font-semibold text-indigo-300 transition hover:bg-indigo-500/10"
            >
              Analisar meu desempenho
            </button>

            <button
              onClick={() => abrirProfessor("plano_estudo")}
              className="rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-400"
            >
              Montar meu plano
            </button>
          </div>
        </div>
      </section>

      <Dialog
        open={prof.open}
        onOpenChange={(open) => setProf((current) => ({ ...current, open }))}
      >
        <DialogContent className="max-h-[80vh] overflow-y-auto border-slate-700 bg-slate-900 text-white sm:max-w-lg">
          <DialogTitle className="flex items-center gap-2 text-indigo-300">
            <Bot className="h-5 w-5" />
            Professor ApoiaConcursos IA
          </DialogTitle>

          {prof.loading ? (
            <div className="flex items-center gap-2 py-8 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              Pensando…
            </div>
          ) : (
            <div
              className="whitespace-pre-wrap text-sm leading-relaxed text-slate-200"
              data-testid="home-professor-response"
            >
              {prof.text}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
